import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { getStudentAvatar } from '../../../shared/studentAvatar';
import StudentAvatar from '../../../shared/StudentAvatarView';
import {
  MAX_VISIBLE_STARS,
  calcStars,
  clampStarsForDisplay,
  getCardBadges,
  getLevelLabel,
  getMilestoneBadgeInfo,
  getPagesToNextStar,
  getPeriodStats,
} from '../utils/libraryScoring';

const LibraryStudentCard = ({ student, periodKey, gradeLevel = 1, isTopGrowth = false }) => {
  const stats = useMemo(() => getPeriodStats(student, periodKey), [student, periodKey]);
  const safeGradeLevel = Number(gradeLevel) > 0 ? Number(gradeLevel) : 1;
  const stars = useMemo(() => calcStars(stats, safeGradeLevel), [stats, safeGradeLevel]);
  const filledStars = useMemo(() => clampStarsForDisplay(stars), [stars]);
  const overflowStars = useMemo(() => Math.max(0, stars - MAX_VISIBLE_STARS), [stars]);
  const pagesToNextStar = useMemo(() => getPagesToNextStar(stats, safeGradeLevel), [stats, safeGradeLevel]);
  const levelLabel = useMemo(() => getLevelLabel(stars), [stars]);
  const milestone = useMemo(() => getMilestoneBadgeInfo(student, safeGradeLevel), [student, safeGradeLevel]);
  const badges = useMemo(
    () => getCardBadges({ student, periodKey, isTopGrowth, gradeLevel: safeGradeLevel }),
    [student, periodKey, isTopGrowth, safeGradeLevel],
  );

  const avatar = getStudentAvatar(student.id, student.avatar_id, student.starter_emoji);

  return (
    <Paper
      className="library-student-card"
      elevation={3}
      sx={{
        bgcolor: avatar.bgColor,
        borderRadius: 4,
        p: { xs: 1.8, md: 2.2 },
        minHeight: { xs: 190, md: 210 },
        width: '100%',
        maxWidth: 560,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 1,
        border: milestone.gainedThisWeek ? '2px solid rgba(245, 158, 11, 0.85)' : '1px solid rgba(0,0,0,0.06)',
        boxShadow: milestone.gainedThisWeek
          ? '0 8px 26px rgba(245, 158, 11, 0.28)'
          : '0 4px 14px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <StudentAvatar studentId={student.id} avatarId={student.avatar_id} starterEmoji={student.starter_emoji}
          name={student.firstName} size={48} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 'clamp(22px, 2.4vw, 34px)', lineHeight: 1.1 }}>
            {student.firstName}
          </Typography>
          {milestone.badgeCount > 0 && (
            <Chip
              label={`🏅 x${milestone.badgeCount}`}
              size="small"
              sx={{
                height: 'clamp(22px, 1.7vw, 28px)',
                borderRadius: 2.5,
                bgcolor: 'rgba(255, 214, 102, 0.35)',
                border: '1px solid rgba(245, 158, 11, 0.45)',
                '& .MuiChip-label': {
                  px: 1,
                  fontWeight: 800,
                  fontSize: 'clamp(11px, 0.9vw, 13px)',
                },
              }}
            />
          )}
          {milestone.gainedThisWeek && (
            <Chip
              label={`✨ Bu hafta +${milestone.gainedCountThisWeek}`}
              size="small"
              sx={{
                height: 'clamp(22px, 1.7vw, 28px)',
                borderRadius: 2.5,
                bgcolor: 'rgba(255, 244, 214, 0.9)',
                border: '1px solid rgba(245, 158, 11, 0.55)',
                '& .MuiChip-label': {
                  px: 1,
                  fontWeight: 800,
                  fontSize: 'clamp(11px, 0.9vw, 13px)',
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: 'clamp(16px, 1.35vw, 21px)', textAlign: 'center' }}>
        {stats.pages} sayfa • {stats.books} kitap
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.65 }}>
        <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 'clamp(12px, 1.05vw, 15px)' }}>
          {levelLabel}
        </Typography>

        <Box className="library-student-stars" sx={{ display: 'flex', alignItems: 'center', minHeight: 34, flexWrap: 'wrap', justifyContent: 'center', gap: 0.25 }}>
          {Array.from({ length: MAX_VISIBLE_STARS }).map((_, idx) => (
            <Box
              key={idx}
              component="span"
              sx={{
                fontSize: 'clamp(20px, 1.65vw, 28px)',
                mr: 0.25,
                filter: idx < filledStars ? 'none' : 'grayscale(1) opacity(0.28)',
              }}
            >
              ⭐
            </Box>
          ))}
          {overflowStars > 0 && (
            <Typography sx={{ ml: 0.6, fontWeight: 800, fontSize: 'clamp(14px, 1.15vw, 17px)', color: 'text.secondary' }}>
              +{overflowStars}
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 'clamp(12px, 1vw, 14px)' }}>
          Sonraki yıldıza: {pagesToNextStar} sayfa
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.95, minHeight: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
          {badges.map((badgeItem) => (
            <Chip
              key={badgeItem.key}
              label={`${badgeItem.icon} ${badgeItem.label} x${badgeItem.mult}`}
              size="small"
              sx={{
                height: 'clamp(23px, 1.7vw, 29px)',
                borderRadius: 2.5,
                '& .MuiChip-label': {
                  px: 1.05,
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 0.95vw, 14px)',
                },
                bgcolor: 'rgba(255,255,255,0.72)',
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default LibraryStudentCard;
